/*
  To trace accross services, we need to propagate the trace ID and span ID.
  To do so, we pass them as headers (or query string when headers are not available).

  We also support the traceparent header, which is the standard way to propagate the trace ID and span ID.
  To ensure compatibility with services like Cloud Run.
 */

import type { TraceState } from './types';

interface DistributedTracingContext {
    spanId: string;
    traceId: string;
    traceFlags: number;
    state: TraceState;
}

const TRACEPARENT_HEADER = 'traceparent';
const TRACESTATE_HEADER = 'tracestate';

/**
 * Extract the trace ID from the HTTP headers.
 */
export function getTraceFromHeaders(
    headers: {
        [key: string]: any;
    },

    /** When provided, headers can be extracted from the query parameter. */
    url?: string
): DistributedTracingContext | null {
    const parsedUrl = url ? new URL(url) : null;

    const rawState = headers[TRACESTATE_HEADER] ?? parsedUrl?.searchParams.get(TRACESTATE_HEADER);
    const state = rawState ? parseTracestate(rawState) : {};

    const traceparent = headers[TRACEPARENT_HEADER];
    if (typeof traceparent === 'string') {
        const trace = parseTraceparent(traceparent);
        if (trace) {
            return {
                ...trace,
                state,
            };
        }
    }

    return null;
}

/**
 * Get the HTTP headers to propagate the trace.
 */
export function getHeadersForTrace(trace: DistributedTracingContext): { [key: string]: string } {
    const traceState = formatTracestate(trace.state);

    return {
        [TRACEPARENT_HEADER]: formatTraceparent(trace),
        ...(traceState ? { [TRACESTATE_HEADER]: traceState } : {}),
    };
}

/**
 * Format the traceparent header.
 * https://www.w3.org/TR/trace-context/
 */
function formatTraceparent(trace: DistributedTracingContext): string {
    return `00-${trace.traceId}-${trace.spanId}-${trace.traceFlags.toString(16).padStart(2, '0')}`;
}

function parseTraceparent(
    traceparent: string
): Pick<DistributedTracingContext, 'spanId' | 'traceId' | 'traceFlags'> | undefined {
    const [_, traceId, spanId, flags] = traceparent.split('-');
    if (!traceId || !spanId || !flags) {
        return undefined;
    }

    const traceFlags = Number.parseInt(flags, 16);
    if (!Number.isInteger(traceFlags) || traceFlags < 0 || traceFlags > 0xff) {
        return undefined;
    }

    return {
        spanId,
        traceFlags,
        traceId,
    };
}

/**
 * Format the tracestate header.
 * https://www.w3.org/TR/trace-context/#tracestate-header
 */
function formatTracestate(state: TraceState): string {
    const entries = Object.entries(state);
    if (entries.length === 0) {
        return '';
    }

    return entries.map(([key, value]) => `${key}=${value}`).join(',');
}

function parseTracestate(tracestate: string): TraceState {
    const entries = tracestate.split(',');
    if (entries.length === 0) {
        return {};
    }

    const state: TraceState = {};
    for (const entry of entries) {
        const [key, value] = entry.split('=');
        if (!key || !value) {
            continue;
        }

        state[key] = value;
    }

    return state;
}
